import { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VegetationMapControl from "./VegetationMapControl";
import UrbanMapControl from "./UrbanMapControl";
import StatewideControl from "./StatewideControl";
import BaseLayer from "./BaseLayer";
import { TILE_VERSIONS } from "constants/tileVersions";

const MapControlsAccordion = ({
  type = "vegetation",
  compareMode,
  setCompareMode,
  tileVersion,
  setTileVersion,
  leftVersion,
  setLeftVersion,
  tilesScenario,
  setTilesScenario,
  tilesFilterLayers,
  setTilesFilterLayers,
}) => {
  const [expanded, setExpanded] = useState("panel0");

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const panels = [];

  if (type === "vegetation") {
    panels.push({
      title: "Pathways Model",
      content: <VegetationMapControl />,
    });
  } else if (type === "urban") {
    panels.push({
      title: "Intermix Model",
      content: <UrbanMapControl />,
    });
  } else if (type === "risk") {
    panels.push({
      title: "Risk Viewer",
      content: (
        <>
          <FormControlLabel
            control={
              <Checkbox
                checked={compareMode}
                onChange={(e) => setCompareMode(e.target.checked)}
                color="primary"
              />
            }
            label="Compare Versions"
            labelPlacement="start"
            sx={{
              margin: 0,
              width: "100%",
              justifyContent: "space-between",
              mb: 2,
            }}
          />

          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {compareMode ? "Right Version" : "Tile Version"}
          </Typography>
          <Select
            value={tileVersion}
            onChange={(e) => setTileVersion(e.target.value)}
            variant="standard"
            fullWidth
            sx={{ mb: 2 }}
          >
            {TILE_VERSIONS.map((version) => (
              <MenuItem key={version.value} value={version.value}>
                {version.label}
              </MenuItem>
            ))}
          </Select>

          {compareMode && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Left Version
              </Typography>
              <Select
                value={leftVersion}
                onChange={(e) => setLeftVersion(e.target.value)}
                variant="standard"
                fullWidth
                sx={{ mb: 2 }}
              >
                {TILE_VERSIONS.map((version) => (
                  <MenuItem key={version.value} value={version.value}>
                    {version.label}
                  </MenuItem>
                ))}
              </Select>
            </>
          )}
        </>
      ),
    });
  }

  panels.push({
    title: "Statewide Data",
    content: (
      <StatewideControl
        tileVersion={type === "risk" ? tileVersion : TILE_VERSIONS[0].value}
        compareMode={type === "risk" ? compareMode : false}
        scenario={tilesScenario}
        onScenarioChange={setTilesScenario}
        layers={tilesFilterLayers}
        onLayersChange={setTilesFilterLayers}
        skipMapUpdates={type === "risk"}
      />
    ),
  });

  panels.push({
    title: "Map Base Layer",
    content: <BaseLayer />,
    alwaysExpanded: true,
  });

  return (
    <Box
      sx={{
        position: "absolute",
        top: "70px",
        right: "10px",
        width: "275px",
        zIndex: 1000,
        maxHeight: "calc(100vh - 80px)",
        overflowY: "auto",
      }}
    >
      {panels.map((panel, index) => (
        <Accordion
          key={index}
          expanded={panel?.alwaysExpanded || expanded === `panel${index}`}
          onChange={handleChange(`panel${index}`)}
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            boxShadow: (theme) => theme.shadows[1],
            "&:before": {
              display: "none",
            },
            "&.Mui-expanded": {
              margin: 0,
            },
            borderBottom: "none",
          }}
        >
          <AccordionSummary
            expandIcon={panel?.alwaysExpanded || <ExpandMoreIcon />}
            sx={{
              minHeight: "48px",
              "&.Mui-expanded": {
                minHeight: "48px",
              },
              "& .MuiAccordionSummary-content": {
                margin: "12px 0",
                "&.Mui-expanded": {
                  margin: "12px 0",
                },
              },
            }}
          >
            <Typography variant="h5">{panel.title}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>{panel.content}</AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default MapControlsAccordion;
